import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { Suspense, useEffect } from 'react'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { QueryClientProvider } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { AppShell } from '@/components/layout'
import { AuthProvider, ProtectedRoute, PublicRoute } from '@/components/auth'
import { UpdateRequiredGate } from '@/components/app/UpdateRequiredGate'
import { ErrorBoundary, NotFoundPage } from '@/components/error'
import { isFeatureHidden } from '@/config/features'
import { useOAuthReturn } from '@/hooks/useOAuthReturn'
import { useUniversalLinkRouting } from '@/hooks/useUniversalLinkRouting'
import { ToastContainer } from '@/components/ui/toast'
import { queryClient } from '@/lib/queryClient'
import { tokenManager } from '@/api/client'
import { useAuthStore, selectUser } from '@/stores'

// Lazy-loaded pages — each becomes its own chunk (moved to routes/lazyPages.ts
// to keep this route table under the house 600-line file limit).
import {
  AcceptInvitePage,
  AdminBusinessDetailPage,
  AdminDashboardPage,
  AdsPerformancePage,
  AdsSettingsPage,
  AnalyticsPage,
  AttributionLinksPage,
  AuthAcceptPage,
  BillingCancelPage,
  BillingSuccessPage,
  BookingsCalendarPage,
  CampaignDetailPage,
  CampaignEditorPage,
  CampaignsListPage,
  CampaignsPage,
  ChatPage,
  AgentRoleChatPage,
  ContactsPage,
  ContentCalendarPage,
  ContentPage,
  CourseOverviewPage,
  CoursesPage,
  PromptLibraryPage,
  InboxPage,
  DashboardPage,
  ForgotPasswordPage,
  GmbInsightsPage,
  GmbPostsPage,
  LeadDetailPage,
  LeadTriagePage,
  LeadsListPage,
  LessonPage,
  LibraryPage,
  LoginPage,
  MfaVerifyPage,
  PreviewPage,
  OfferPage,
  UploadLinkPage,
  StatusPage,
  SettingsLayout,
  SettingsIndex,
  BusinessPage,
  AccountsPage,
  TeamPage,
  PlanPage,
  ProfilePage,
  AdvancedPage,
  VoiceClonePage,
  FilesPage,
  ApprovalsPage,
  ReviewPage,
  NotificationsPage,
  OAuthCallbackPage,
  OAuthConsentPage,
  OnboardingPage,
  ShopifyInstallPage,
  RegisterPage,
  ResetPasswordPage,
  SmsCampaignComposePage,
  MediaLibraryPage,
  VerifyEmailPage,
  ShopifyPage,
  ShopifyInsightsPage,
  VideoEditorPage,
  ImageEditorPage,
} from '@/routes/lazyPages'

function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Loader2 className="text-primary h-6 w-6 animate-spin" />
    </div>
  )
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore(selectUser)
  const isAdmin = !!(user as Record<string, unknown> | null)?.is_admin
  if (!isAdmin) return <NotFoundPage />
  return <>{children}</>
}

function HiddenFeatureRoute({
  featureKey,
  children,
}: {
  featureKey: string
  children: React.ReactNode
}) {
  if (isFeatureHidden(featureKey)) return <Navigate to="/chat" replace />
  return <>{children}</>
}

export function DevLogin() {
  const navigate = useNavigate()
  useEffect(() => {
    // tokenManager, not raw localStorage — on native the tokens live in the
    // Keychain/Keystore (secureTokenStorage.ts) and a direct localStorage
    // write is invisible to every authenticated request.
    tokenManager.setTokens('mock-access-token-dev', 'mock-refresh-token-dev')
    // Seed the Zustand store directly so AuthProvider doesn't try to validate
    // against a possibly-down backend and wipe the session via logout().
    useAuthStore.getState().setUser({
      id: 'dev',
      email: 'dev@maxflow-dev.com',
      default_business_id: '6330cff3-915b-4ef6-bf14-25a9485ca470',
    } as never)
    useAuthStore.setState({
      onboarding: { completed: true, currentStep: 999 },
    })
    // Chat is home (#2567).
    navigate('/chat', { replace: true })
  }, [navigate])
  return <div style={{ padding: 40 }}>Logging in as dev user...</div>
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ''}>
        <BrowserRouter>
          <ErrorBoundary>
            <UpdateRequiredGate>
              <Routes>
                {/* Dev-only routes — outside AuthProvider */}
                {import.meta.env.DEV && (
                  <>
                    <Route path="dev-login" element={<DevLogin />} />
                  </>
                )}
                <Route path="*" element={<AuthenticatedApp />} />
              </Routes>
            </UpdateRequiredGate>
          </ErrorBoundary>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  )
}

function AuthenticatedApp() {
  // Native OAuth returns arrive as io.maxflow.app://oauth-callback deep links;
  // route them inside the app so the callback screen sees the live session.
  useOAuthReturn()

  // Universal/App Link deep links (https://app.maxflow.io/...) from push or
  // email open the matching in-app screen instead of a browser tab (#2573).
  useUniversalLinkRouting()

  return (
    <AuthProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes - redirect to dashboard if authenticated */}
          <Route
            path="login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route
            path="login/mfa"
            element={
              <PublicRoute>
                <MfaVerifyPage />
              </PublicRoute>
            }
          />
          <Route
            path="forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="reset-password"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="verify-email"
            element={
              <PublicRoute>
                <VerifyEmailPage />
              </PublicRoute>
            }
          />
          <Route path="auth/accept" element={<AuthAcceptPage />} />
          {/* Emailed staff-invite link (#2634) — accessible whether logged in
              or not, since an already-logged-in matching account still needs
              to accept. */}
          <Route path="accept-invite/:token" element={<AcceptInvitePage />} />
          {/* Public status page (#2572) — accessible whether logged in or not. */}
          <Route path="status" element={<StatusPage />} />
          {/* Exact post preview (#2822) — public, view-only; the token is the access. */}
          <Route path="p/:token" element={<PreviewPage />} />
          {/* An offer's public sign-up page — public; the slug is the address. */}
          <Route path="offer/:slug" element={<OfferPage />} />
          {/* SMS upload link — public; the token is the access. */}
          <Route path="u/:token" element={<UploadLinkPage />} />
          {/* Onboarding - protected but separate from main app */}
          <Route
            path="onboarding"
            element={
              <ProtectedRoute key="onboarding">
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          {/* MCP OAuth consent (#2166) — MaxFlow is the authorization SERVER here.
            Protected (it needs the session) but full-screen: the user is mid-flow
            in an external MCP client, not browsing the app. Distinct from the
            social-connections callback at settings/connections/oauth-callback,
            where we are the OAuth client. */}
          <Route
            path="oauth/consent"
            element={
              <ProtectedRoute key="mcp-consent">
                <OAuthConsentPage />
              </ProtectedRoute>
            }
          />
          {/* Lead triage focus mode - protected, full-screen (no AppShell chrome).
            Registered before leads/:id so the static segment wins. */}
          <Route
            path="leads/triage"
            element={
              <ProtectedRoute key="triage">
                <LeadTriagePage />
              </ProtectedRoute>
            }
          />
          {/* Image editor - protected, full-screen (no AppShell chrome). */}
          <Route
            path="library/image/:draftId/edit"
            element={
              <ProtectedRoute key="image-editor">
                <ImageEditorPage />
              </ProtectedRoute>
            }
          />
          {/* Protected routes - require authentication */}
          <Route
            element={
              <ProtectedRoute key="app">
                <AppShell />
              </ProtectedRoute>
            }
          >
            {/* Chat is home (#2567) — the chat-first pivot. */}
            <Route index element={<Navigate to="/chat" replace />} />
            {/* Dashboard predates Today (#2567) and is no longer in primary nav —
                kept reachable at its old URL rather than deleted outright. */}
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="chat/:roleId" element={<AgentRoleChatPage />} />
            <Route path="learning" element={<CoursesPage />} />
            <Route path="learning/prompts" element={<PromptLibraryPage />} />
            <Route path="learning/:slug" element={<CourseOverviewPage />} />
            <Route path="learning/:slug/:sectionSlug" element={<LessonPage />} />
            <Route path="leads" element={<LeadsListPage />} />
            <Route path="leads/:id" element={<LeadDetailPage />} />
            <Route path="crm/contacts" element={<ContactsPage />} />
            <Route path="crm/bookings" element={<BookingsCalendarPage />} />
            {/* Campaigns — one goal over a period, delivered across a mix of
                channels; a flat card list filtered by category, with a
                single-campaign detail page for its pieces and results. */}
            <Route
              path="campaigns"
              element={
                <ErrorBoundary variant="inline">
                  <CampaignsPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="campaigns/:id"
              element={
                <ErrorBoundary variant="inline">
                  <CampaignDetailPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="campaigns/sms/new"
              element={
                <ErrorBoundary variant="inline">
                  <SmsCampaignComposePage />
                </ErrorBoundary>
              }
            />
            {/* The unified content grid. `content/:kind/:id` opens the same
                page with a detail view — it is 3 segments, so it cannot
                collide with the 2-segment routes below, and React Router
                ranks the static `content/video-editor/:projectId` above it. */}
            <Route
              path="content"
              element={
                <ErrorBoundary variant="inline">
                  <ContentPage />
                </ErrorBoundary>
              }
            />
            <Route path="calendar" element={<ContentCalendarPage />} />
            <Route path="content/video-editor/:projectId" element={<VideoEditorPage />} />
            <Route path="media" element={<MediaLibraryPage />} />
            <Route path="files" element={<FilesPage />} />
            {/* Library (#2567) — one nav entry grouping Files, Media and the
                Content library. /files stays reachable directly (deep links)
                but the nav points here. */}
            <Route path="library" element={<LibraryPage />} />
            <Route
              path="content/:kind/:id"
              element={
                <ErrorBoundary variant="inline">
                  <ContentPage />
                </ErrorBoundary>
              }
            />
            <Route path="gmb/posts" element={<GmbPostsPage />} />
            <Route path="gmb/insights" element={<GmbInsightsPage />} />
            <Route
              path="ads/campaigns"
              element={
                <HiddenFeatureRoute featureKey="ads">
                  <CampaignsListPage />
                </HiddenFeatureRoute>
              }
            />
            <Route
              path="ads/performance"
              element={
                <HiddenFeatureRoute featureKey="ads">
                  <AdsPerformancePage />
                </HiddenFeatureRoute>
              }
            />
            <Route
              path="ads/campaigns/new"
              element={
                <HiddenFeatureRoute featureKey="ads">
                  <CampaignEditorPage />
                </HiddenFeatureRoute>
              }
            />
            {/* Not gated by the `ads` build flag: a campaign's ad pieces must
                stay reachable from its detail page regardless of that
                deployment-wide flag — the flag governs the standalone Ads
                suite (list/new/performance/settings), not viewing a piece
                that already belongs to a campaign. */}
            <Route path="ads/campaigns/:id" element={<CampaignEditorPage />} />
            <Route path="ads/campaigns/:id/edit" element={<CampaignEditorPage />} />
            <Route
              path="ads/settings"
              element={
                <HiddenFeatureRoute featureKey="ads">
                  <AdsSettingsPage />
                </HiddenFeatureRoute>
              }
            />
            <Route
              path="attribution"
              element={
                <HiddenFeatureRoute featureKey="attribution">
                  <AttributionLinksPage />
                </HiddenFeatureRoute>
              }
            />
            <Route
              path="attribution/links"
              element={
                <HiddenFeatureRoute featureKey="attribution">
                  <AttributionLinksPage />
                </HiddenFeatureRoute>
              }
            />
            <Route
              path="results"
              element={
                <ErrorBoundary variant="inline">
                  <AnalyticsPage />
                </ErrorBoundary>
              }
            />
            {/* Settings: one list of pages (settingsPages.ts); /settings is the list on a phone. */}
            <Route element={<SettingsLayout />}>
              <Route path="settings" element={<SettingsIndex />} />
              <Route path="settings/business" element={<BusinessPage />} />
              <Route path="settings/accounts" element={<AccountsPage />} />
              <Route path="settings/team" element={<TeamPage />} />
              <Route path="settings/plan" element={<PlanPage />} />
              <Route path="settings/profile" element={<ProfilePage />} />
              <Route path="settings/advanced" element={<AdvancedPage />} />
              <Route path="settings/voice-clone" element={<VoiceClonePage />} />
            </Route>
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route path="review/:pageId" element={<ReviewPage />} />
            <Route path="settings/connections/oauth-callback" element={<OAuthCallbackPage />} />
            {/* Shopify's App URL points here — a merchant-initiated install arrives
              with ?shop=… and no session; ProtectedRoute round-trips it through
              login so the install starts with a real business context. */}
            <Route path="settings/connections/shopify/install" element={<ShopifyInstallPage />} />
            <Route path="billing/success" element={<BillingSuccessPage />} />
            <Route path="billing/cancel" element={<BillingCancelPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            {/* Shopify catalog */}
            <Route path="shopify" element={<ShopifyPage />} />
            <Route path="shopify/insights" element={<ShopifyInsightsPage />} />
            {/* Inbox (unified reply surface) */}
            <Route path="inbox" element={<InboxPage />} />
            {/* Admin */}
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/businesses/:businessId"
              element={
                <AdminRoute>
                  <AdminBusinessDetailPage />
                </AdminRoute>
              }
            />
            {/* 404 page for unknown routes within protected area */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          {/* 404 page for completely unknown routes */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <ToastContainer />
    </AuthProvider>
  )
}

export default App
